// Reglages personnels · Notifications (08/10) : la section native de Dust, ramenee a ce qui marche
// sur la pile astraly (courriels par notre SMTP ; Novu, la fenetre dans l'application, les sons et
// Slack sont inertes). Memes composants que la section amont (SettingsList, interrupteur, menus) ;
// les valeurs et les cles sont dans front/lib/compta/reglages_notifications.ts.
import { MODAL_SETTINGS_LIST_CLASSES } from "@app/components/me/modalSettingsList";
import { useSendNotification } from "@app/hooks/useNotification";
import { t } from "@app/lib/compta/i18n";
import type { ReglagesNotifications } from "@app/lib/compta/reglages_notifications";
import {
  CLES_REGLAGES_NOTIFICATIONS,
  changementsAEnregistrer,
  interrupteurCourrielsVisible,
  LIBELLES_CONDITION,
  LIBELLES_DELAI,
  lireReglagesNotifications,
  OPTIONS_CONDITION,
  OPTIONS_DELAI,
} from "@app/lib/compta/reglages_notifications";
import { useUserMetadata } from "@app/lib/swr/user";
import { setUserMetadataFromClient } from "@app/lib/user";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import {
  Button,
  ContentMessageInline,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InfoCircle,
  SettingsList,
  SliderToggle,
  Spinner,
} from "@dust-tt/sparkle";
import type React from "react";
import { useState } from "react";

interface CadreProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

interface SectionNotificationsCourrielProps {
  /** Le cadre de section de la fenetre des reglages (titre, contenu, pied avec Enregistrer). */
  Cadre: React.ComponentType<CadreProps>;
}

export function SectionNotificationsCourriel({
  Cadre,
}: SectionNotificationsCourrielProps) {
  const sendNotification = useSendNotification();
  const desactives = useUserMetadata(CLES_REGLAGES_NOTIFICATIONS.desactives);
  const condition = useUserMetadata(CLES_REGLAGES_NOTIFICATIONS.condition);
  const delai = useUserMetadata(CLES_REGLAGES_NOTIFICATIONS.delai);
  const [brouillon, setBrouillon] = useState<ReglagesNotifications | null>(
    null
  );
  const [enregistrement, setEnregistrement] = useState(false);
  // Comme la section amont : les menus deroulants s'ouvrent dans le body, au-dessus de la fenetre.
  const [conteneurPortail] = useState<HTMLElement | undefined>(() =>
    typeof document !== "undefined" ? document.body : undefined
  );

  const chargement =
    desactives.isMetadataLoading ||
    condition.isMetadataLoading ||
    delai.isMetadataLoading;
  const enregistres = lireReglagesNotifications({
    desactives: desactives.metadata?.value,
    condition: condition.metadata?.value,
    delai: delai.metadata?.value,
  });
  const courant = brouillon ?? enregistres;
  const changements = changementsAEnregistrer(enregistres, courant);

  const modifier = (partiel: Partial<ReglagesNotifications>) =>
    setBrouillon({ ...courant, ...partiel });

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      for (const changement of changements) {
        await setUserMetadataFromClient(changement);
      }
      await Promise.all([
        desactives.mutateMetadata(),
        condition.mutateMetadata(),
        delai.mutateMetadata(),
      ]);
      setBrouillon(null);
      sendNotification({
        type: "success",
        title: t("Notification preferences saved"),
      });
    } catch (error) {
      sendNotification({
        type: "error",
        title: t("Error updating notification preferences"),
        description: normalizeError(error).message,
      });
    } finally {
      setEnregistrement(false);
    }
  };

  const conditionInactive = !courant.courrielsActifs;
  const delaiInactif =
    !courant.courrielsActifs || courant.condition === "never";

  return (
    <Cadre
      title={t("Notifications")}
      description={t("Choose when you receive an email")}
      footer={
        <Button
          label={t("Save")}
          variant="primary"
          type="button"
          onClick={enregistrer}
          disabled={changements.length === 0 || enregistrement || chargement}
        />
      }
    >
      {chargement ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <ContentMessageInline variant="info" icon={InfoCircle}>
            {t(
              "Notifications are sent by email. In-app popups and sounds are not available."
            )}
          </ContentMessageInline>
          <SettingsList className={MODAL_SETTINGS_LIST_CLASSES}>
            {interrupteurCourrielsVisible() && (
              <SettingsList.Row
                title={t("Email notifications")}
                description={t(
                  "Mentions, replies in your conversations and results of your scheduled tasks"
                )}
                action={
                  <SliderToggle
                    selected={courant.courrielsActifs}
                    onClick={() =>
                      modifier({ courrielsActifs: !courant.courrielsActifs })
                    }
                  />
                }
              />
            )}
            <SettingsList.Row
              title={t("Notify me about")}
              description={t(
                "In your conversations. A conversation where you are the only member always notifies you, unless you choose Nothing."
              )}
              action={
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={conditionInactive}>
                    <Button
                      variant="outline"
                      size="sm"
                      isSelect
                      disabled={conditionInactive}
                      label={t(LIBELLES_CONDITION[courant.condition])}
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent mountPortalContainer={conteneurPortail}>
                    {OPTIONS_CONDITION.map((option) => (
                      <DropdownMenuItem
                        key={option}
                        label={t(LIBELLES_CONDITION[option])}
                        onClick={() => modifier({ condition: option })}
                      />
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              }
            />
            <SettingsList.Row
              title={t("Email frequency")}
              description={t("At most one email per conversation")}
              action={
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={delaiInactif}>
                    <Button
                      variant="outline"
                      size="sm"
                      isSelect
                      disabled={delaiInactif}
                      label={t(LIBELLES_DELAI[courant.delai])}
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent mountPortalContainer={conteneurPortail}>
                    {OPTIONS_DELAI.map((option) => (
                      <DropdownMenuItem
                        key={option}
                        label={t(LIBELLES_DELAI[option])}
                        onClick={() => modifier({ delai: option })}
                      />
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              }
            />
          </SettingsList>
          {courant.courrielsActifs && (
            <p className="copy-sm text-muted-foreground">
              {t(
                "Scheduled task results are emailed after each run, whatever you choose above."
              )}
            </p>
          )}
        </div>
      )}
    </Cadre>
  );
}
